import { z } from 'zod'
import { formatPhone, isValidPhone, normalizePhone } from '@rentflowdev/common/phone'

/**
 * Phone validation shared by every form that takes one.
 *
 * Counts digits rather than demanding a canonical format, so the shapes people
 * actually type all pass: +7 (921) 457-00-57, 89214570057, 8 921 457 00 57.
 * Character-length checks are what this replaced — `min(10)` on the raw string
 * happily accepted "уцауца23423", which is why the field looked validated and
 * was not.
 *
 * The rule itself is THE gateway's rule, imported from the pure
 * `@rentflowdev/common/phone` subpath (same pattern as `/vat` and
 * `/currency`) — an earlier second copy here drifted from the server the very
 * first time the two were edited.
 */
export { formatPhone, isValidPhone, normalizePhone }

/** Optional phone: an empty string passes, anything typed must be a real number. */
export function optionalPhone(invalidMessage: string) {
	return z
		.string()
		.trim()
		.refine((v) => v === '' || isValidPhone(v), invalidMessage)
		.optional()
}

/**
 * Same rule, but the field stays a plain `string`. For forms whose value type
 * is not optional and which would otherwise have to widen it just to validate.
 */
export function phoneOrEmpty(invalidMessage: string) {
	return z
		.string()
		.trim()
		.refine((v) => v === '' || isValidPhone(v), invalidMessage)
}

/** Required phone: must be present and valid. */
export function requiredPhone(invalidMessage: string) {
	return z.string().trim().refine(isValidPhone, invalidMessage)
}

/**
 * Whether a stored number can be handed to the masked input untouched.
 *
 * `PhoneInput` re-runs its mask over whatever value it is given on mount, and
 * imask applies the dynamic mask's own `prepare` rather than the chosen child
 * mask's — so the rule that swallows a leading `8` never runs for a
 * programmatically set value, and `89991234567` comes back out shifted as
 * `+7 (899) 912-34-56`. Only the canonical `+` and digits round-trips through
 * both branches unchanged; anything else keeps a plain input rather than being
 * silently rewritten into a number nobody typed.
 */
export function isMaskSafePhone(value?: string | null): boolean {
	return !value || /^\+\d{8,15}$/.test(value)
}
